import { roundToAvailableLoad } from '../gym/round-load.js';
import { regionVolumeFactor, sessionModulation } from '../readiness/score.js';
import { SAFETY_LIMITS, clampSessionLoad } from '../safety/limits.js';
import { daysBetweenLocal } from '../units/local-date.js';
import { roundKg, roundTo } from '../units/round.js';
import { e1rmConfidence, e1rmEpleyRir, estimateCurrentE1rm, loadForTarget } from './e1rm.js';
import { applyStrategy, resolveTargetReps } from './strategies.js';
import {
  LOAD_ENGINE_VERSION,
  type BlockState,
  type ExerciseHistory,
  type ExerciseSessionSummary,
  type LoadRecommendation,
  type PerformedSetSummary,
  type ProgressionState,
  type ReadinessSummary,
  type RepTarget,
  type SetType,
} from './types.js';

/**
 * Motor de recomendacion de carga (docs/LOAD_RECOMMENDATION_ENGINE.md §3).
 *
 * Funcion PURA: mismas entradas -> misma salida. Sin red, sin base de datos y
 * sin reloj del sistema. La carga se muestra siempre como recomendacion, nunca
 * como imposicion (requisito §11).
 */
/**
 * Vuelta a un ejercicio tras una pausa (encargo del 23/09/2026, §7.6: «tras
 * sustitución o pausa relevante, recalibra»).
 *
 * Hasta entonces el motor no miraba el calendario: tres meses sin hacer un
 * ejercicio y proponía a partir de la última sesión como si fuera de la semana
 * pasada, con el suelo del ±10 % impidiendo bajar más. Ahora, si la última
 * sesión comparable tiene `minDaysSinceLast` días o más, la carga se toma como
 * una recalibración: se descuenta `loadFactor`, nunca sube respecto a la
 * última vez, deja de aplicarse el suelo del −10 % y la confianza se limita.
 *
 * Los números son una CONVENCIÓN prudente, no un umbral fisiológico: la fuerza
 * se conserva bien unas semanas sin entrenar y se pierde de forma variable
 * después. Se revisan aquí, y en `LOAD_RECOMMENDATION_ENGINE.md` §4 bis.
 */
export const RETURN_AFTER_PAUSE = {
  minDaysSinceLast: 21,
  loadFactor: 0.9,
  maxConfidence: 0.5,
} as const;

export interface LoadRecommendationInput {
  target: RepTarget;
  setIndex: number;
  totalSets: number;
  setType: SetType;
  history: ExerciseHistory;
  progression: ProgressionState;
  readiness: ReadinessSummary;
  block: BlockState;
  /** Series ya realizadas HOY en este ejercicio. */
  performedToday: PerformedSetSummary[];
  /** Series efectivas ya realizadas hoy sobre los mismos musculos. */
  sessionSetsBefore: number;
  availableLoads: readonly number[];
  /** Si el equipamiento no tiene cargas configuradas, se avisa en vez de inventar. */
  hasConfiguredLoads: boolean;
  /**
   * Día local de hoy. Sin él no se puede saber si hay pausa y no se aplica
   * `RETURN_AFTER_PAUSE`: el dominio no lee el reloj (ADR-007).
   */
  today?: string | undefined;
}

const BLOCK_FACTOR: Record<BlockState['intent'], number> = {
  accumulation: 1,
  intensification: 0.95,
  deload: 0.65,
  reintroduction: 0.9,
};

export function recommendLoad(input: LoadRecommendationInput): LoadRecommendation {
  const reasonCodes: string[] = [];
  const lastSession = input.history.sessions.at(-1);

  // --- 1. Estimacion de e1RM a partir de VARIAS sesiones ---------------------
  const sessionE1rms = input.history.sessions.map((session) => bestE1rmOfSession(session));
  const current = estimateCurrentE1rm(sessionE1rms.filter((entry) => entry !== null));

  // --- 2. Primera vez: no se sugiere carga -----------------------------------
  if (current === null) {
    return {
      loadKg: null,
      rawLoadKg: null,
      confidence: 0,
      reasonCodes: ['first_time'],
      repsAdjustment: 0,
      inputsSnapshot: { target: input.target, sessions: 0 },
      engineVersion: LOAD_ENGINE_VERSION,
    };
  }

  if (current.noisy) reasonCodes.push('noisy_history');

  const diasDePausa =
    input.today !== undefined && lastSession !== undefined
      ? daysBetweenLocal(lastSession.localDate, input.today)
      : 0;
  const trasPausa = diasDePausa >= RETURN_AFTER_PAUSE.minDaysSinceLast;
  if (trasPausa) reasonCodes.push('return_after_pause');

  // --- 3. Carga teorica para el objetivo -------------------------------------
  const progression = applyStrategy(input.progression.strategy, lastSession, input.target);
  const targetReps = resolveTargetReps(input.target, progression.repsHint, lastSession);
  const modulation = sessionModulation(input.readiness.todayScore);

  const rirTarget = input.target.rir + modulation.rirDelta;
  const theoretical = loadForTarget(current.e1rmKg, targetReps, rirTarget);

  // --- 4. Multiplicadores ----------------------------------------------------
  const mProgression = progression.loadFactor;
  const mReadiness = modulation.loadFactor;
  const mPosition = input.setType === 'backoff' ? 0.85 : 1;
  const mIntraFatigue = intraSessionFatigueFactor(input.sessionSetsBefore);
  const mBlock = BLOCK_FACTOR[input.block.intent];
  const mSoreness = regionVolumeFactor(input.readiness.regionSoreness) < 1 ? 0.95 : 1;
  const bias = 1 + input.progression.overrideBiasPct;

  reasonCodes.push(...progression.reasonCodes, ...modulation.reasonCodes);
  if (mIntraFatigue < 1) reasonCodes.push('intra_session_fatigue');
  if (input.block.intent === 'deload') reasonCodes.push('deload_no_increase');
  if (mSoreness < 1) reasonCodes.push('region_soreness');
  // Dos codigos y no uno: hasta que `learnOverrideBias` existio el sesgo valia
  // 0 siempre, asi que un unico motivo escrito en positivo —«sueles usar algo
  // mas de carga»— nunca se veia. Ahora puede ser negativo, y explicar una
  // bajada diciendo que subes es peor que no explicarla.
  if (input.progression.overrideBiasPct >= 0.005) reasonCodes.push('override_bias');
  if (input.progression.overrideBiasPct <= -0.005) reasonCodes.push('override_bias_down');

  let raw =
    theoretical * mProgression * mReadiness * mPosition * mIntraFatigue * mBlock * mSoreness * bias;

  // En deload el motor nunca sube carga aunque el rendimiento lo permita.
  const previousLoad = lastTopLoad(lastSession);
  if (input.block.intent === 'deload' && previousLoad !== null) {
    raw = Math.min(raw, previousLoad);
  }
  // Tras una pausa, lo mismo y además un descuento: la última sesión describe
  // una capacidad que puede no ser la de hoy.
  if (trasPausa) {
    raw *= RETURN_AFTER_PAUSE.loadFactor;
    if (previousLoad !== null) raw = Math.min(raw, previousLoad);
  }

  // --- 5. Limite duro entre sesiones ----------------------------------------
  // Tras una pausa no se aplica el suelo del −10 %: está para que una sesión
  // mala no hunda la siguiente, no para impedir recalibrar.
  const clamped = trasPausa
    ? { value: raw, clamped: false, reasonCodes: [] as string[] }
    : clampSessionLoad(raw, previousLoad);
  if (clamped.clamped) reasonCodes.push(...clamped.reasonCodes);

  // --- 6. Redondeo a carga realmente seleccionable ---------------------------
  if (!input.hasConfiguredLoads || input.availableLoads.length === 0) {
    return {
      loadKg: null,
      rawLoadKg: roundKg(clamped.value),
      confidence: 0.3,
      reasonCodes: [...reasonCodes, 'equipment_not_configured'],
      repsAdjustment: 0,
      inputsSnapshot: snapshot(input, current, theoretical, {
        mProgression,
        mReadiness,
        mPosition,
        mIntraFatigue,
        mBlock,
        bias,
      }),
      engineVersion: LOAD_ENGINE_VERSION,
    };
  }

  // El redondeo se hace DENTRO de los limites de seguridad: elegir la carga mas
  // proxima podria rebasar por arriba el techo entre sesiones ya aplicado.
  const ceiling =
    previousLoad === null
      ? undefined
      : input.block.intent === 'deload' || trasPausa
        ? previousLoad
        : previousLoad * (1 + SAFETY_LIMITS.load.maxSessionToSessionChangePct);
  const floor =
    previousLoad === null || trasPausa
      ? undefined
      : previousLoad * (1 - SAFETY_LIMITS.load.maxSessionToSessionChangePct);

  const rounded = roundToAvailableLoad(clamped.value, input.availableLoads, {
    ...(ceiling === undefined ? {} : { maxKg: ceiling }),
    ...(floor === undefined ? {} : { minKg: floor }),
  });
  if (rounded.coarseIncrement) reasonCodes.push('coarse_increment');
  if (rounded.boundedBySafety) reasonCodes.push('rounded_within_safety_bounds');

  // Con incrementos groseros se compensa el estimulo con +/-1 repeticion.
  const repsAdjustment = rounded.coarseIncrement
    ? rounded.loadKg !== null && rounded.loadKg < clamped.value
      ? 1
      : -1
    : 0;

  const confidence = roundTo(
    Math.min(
      current.confidence,
      input.readiness.todayScore === null ? 0.8 : 0.95,
      trasPausa ? RETURN_AFTER_PAUSE.maxConfidence : 1,
    ) * (current.noisy ? 0.8 : 1),
    2,
  );

  return {
    loadKg: rounded.loadKg,
    rawLoadKg: roundKg(clamped.value),
    confidence,
    reasonCodes,
    repsAdjustment,
    inputsSnapshot: snapshot(input, current, theoretical, {
      mProgression,
      mReadiness,
      mPosition,
      mIntraFatigue,
      mBlock,
      bias,
    }),
    engineVersion: LOAD_ENGINE_VERSION,
  };
}

/** Fatiga acumulada dentro de la sesion: hasta -10 %. */
export function intraSessionFatigueFactor(setsBefore: number): number {
  if (setsBefore <= 4) return 1;
  return Math.max(0.9, 1 - (setsBefore - 4) * 0.01);
}

function bestE1rmOfSession(
  session: ExerciseSessionSummary,
): { at: string; e1rmKg: number; confidence: number } | null {
  let best: { at: string; e1rmKg: number; confidence: number } | null = null;
  for (const set of session.sets) {
    const value = e1rmEpleyRir(set);
    if (value <= 0) continue;
    if (best === null || value > best.e1rmKg) {
      best = { at: session.at, e1rmKg: value, confidence: e1rmConfidence(set) };
    }
  }
  return best;
}

function lastTopLoad(session: ExerciseSessionSummary | undefined): number | null {
  if (!session || session.sets.length === 0) return null;
  return session.sets.reduce((max, set) => Math.max(max, set.loadKg), 0);
}

function snapshot(
  input: LoadRecommendationInput,
  current: { e1rmKg: number; confidence: number; sessionsUsed: number; noisy: boolean },
  theoretical: number,
  multipliers: Record<string, number>,
): Record<string, unknown> {
  return {
    target: input.target,
    setIndex: input.setIndex,
    setType: input.setType,
    strategy: input.progression.strategy,
    e1rmKg: current.e1rmKg,
    e1rmSessions: current.sessionsUsed,
    e1rmNoisy: current.noisy,
    theoreticalLoadKg: theoretical,
    multipliers,
    readinessToday: input.readiness.todayScore,
    regionSoreness: input.readiness.regionSoreness,
    blockIntent: input.block.intent,
    sessionSetsBefore: input.sessionSetsBefore,
  };
}
